import { Injectable } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { IFormConfig } from '@models/form/form';

/**
 * Methods to facilitate using Angular Reactive Forms.
 */
@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(private fb: UntypedFormBuilder) {}

  /**
   * Creates `FormGroup` from the passed in `formConfig`. Currently a naive implementation.
   */
  public buildForm(formConfig: IFormConfig): UntypedFormGroup {
    const form = this.fb.group({});

    formConfig.controls.forEach((formField) => {
      formField.value = formField.value || '';
      return this.addFormControl(
        form,
        formField.name,
        formField.value.toString(),
        formField.validation,
        formField.disabled,
      );
    });
    form.setValidators(Validators.compose([...formConfig.validation]));

    return form;
  }

  /**
   * Will add a `FormControl` to the provided `FormGroup`.
   */
  public addFormControl(
    form: UntypedFormGroup,
    fieldName: string,
    fieldValue: string = '',
    validators: ValidatorFn[] = [],
    disabled: boolean = false,
  ): void {
    // Prevent collisions when creating form values.
    if (form.contains(fieldName)) {
      form.removeControl(fieldName);
    }

    return form.addControl(
      fieldName,
      new UntypedFormControl(
        { value: fieldValue, disabled },
        Validators.compose([...validators]),
      ),
    );
  }

  /**
   * Build a request payload from the matching `FormGroup` values.
   * Does not handle conditional logic.
   */
  public buildRequestPayload<T>(form: UntypedFormGroup, requestPayload: T): T {
    for (const property in requestPayload) {
      if (requestPayload.hasOwnProperty(property)) {
        if (form.controls[property] && form.controls[property].value) {
          requestPayload[property] = form.controls[property].value;
        }
      }
    }
    return requestPayload;
  }

  /**
   * Moves an block in a `FormArray` to another position.
   * Updates the block's `order` property.
   * @param formArray FormArray instance in which to move the item.
   * @param fromIndex Starting index of the item.
   * @param toIndex Index to which he item should be moved.
   */
  public moveBlockInFormArray(
    formArray: UntypedFormArray,
    fromIndex: number,
    toIndex: number,
  ): void {
    const dir = toIndex > fromIndex ? 1 : -1;
    const from = fromIndex;
    const to = toIndex;
    const temp = formArray.at(from);

    for (let i = from; i * dir < to * dir; i = i + dir) {
      const current = formArray.at(i + dir);
      formArray.setControl(i, current);
    }
    formArray.setControl(to, temp);
    this.updateOrder(formArray);
  }

  public removeBlockFromFormArray(
    formArray: UntypedFormArray,
    index: number,
  ): void {
    formArray.removeAt(index);
    this.updateOrder(formArray);
  }

  public clearAllBlocks(formArray: UntypedFormArray): void {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  private updateOrder(formArray: UntypedFormArray): void {
    formArray.controls.forEach((formGroup, index) => {
      formGroup.get('order')?.setValue(index);
    });
  }
}
